import React, { useState, useEffect, useCallback, useMemo } from "react";
import { User, Application } from "@/contracts/auth-api.types";
import { userService } from "@/services/userService";
import { applicationService } from "@/services/applicationService";
import { useNotification } from "@/hooks/useNotification";
import { Button } from "@/components/common/Button";
import { Badge } from "@/components/common/Badge";
import { PermissionGuard } from "@/components/guards/PermissionGuard";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  RefreshCw,
  ShieldAlert,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { formatDate, getErrorMessage } from "@/utils/formatters";
import { UserModal } from "./UserModal";

export const UsersList: React.FC = () => {
  const { notify } = useNotification();
  const [users, setUsers] = useState<User[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTenantFilter, setSelectedTenantFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const [response, appsRes] = await Promise.all([
        userService.list(1, 100),
        applicationService.list(1, 100),
      ]);
      const list = Array.isArray(response) ? response : response.data || [];
      const appList = Array.isArray(appsRes) ? appsRes : appsRes.data || [];
      setUsers(list);
      setApplications(appList);
    } catch (err: unknown) {
      notify(getErrorMessage(err, "Erro ao carregar lista de usuários"), "error");
    } finally {
      setIsLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const appMap = useMemo(() => new Map(applications.map((a) => [a.id, a])), [applications]);

  const filteredUsers = users.filter((u) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term) ||
      (u.username && u.username.toLowerCase().includes(term));
    const matchesTenant =
      selectedTenantFilter === "all" || u.application_id === selectedTenantFilter;
    return matchesSearch && matchesTenant;
  });

  const handleDelete = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await userService.delete(userToDelete.id);
      notify(`Usuário "${userToDelete.name}" removido com sucesso`, "success");
      setUserToDelete(null);
      await loadUsers();
    } catch (err: unknown) {
      notify(getErrorMessage(err, "Erro ao excluir o usuário"), "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Usuários</h1>
          <p className="text-muted text-sm mt-0.5">
            Cadastre e gerencie identidades, perfis de acesso e credenciais dos usuários.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadUsers}
            isLoading={isLoading}
            title="Recarregar lista"
          >
            <RefreshCw size={16} />
            <span className="hide-sm">Recarregar</span>
          </Button>

          <PermissionGuard permission="users:create">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setUserToEdit(null);
                setIsModalOpen(true);
              }}
            >
              <Plus size={16} />
              <span>Novo Usuário</span>
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="search-input-wrapper flex-1 min-w-[220px]">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome, e-mail ou username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {applications.length > 0 && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="user-tenant-filter"
                className="text-xs font-semibold text-muted uppercase tracking-wider shrink-0"
              >
                Tenant:
              </label>
              <select
                id="user-tenant-filter"
                className="input-control text-xs font-medium py-1.5 px-3"
                value={selectedTenantFilter}
                onChange={(e) => setSelectedTenantFilter(e.target.value)}
              >
                <option value="all">Todos os Tenants ({users.length})</option>
                {applications.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name} ({app.client_id})
                  </option>
                ))}
              </select>
            </div>
          )}

          {(searchTerm || selectedTenantFilter !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchTerm("");
                setSelectedTenantFilter("all");
              }}
            >
              Limpar Filtros
            </Button>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading && (
          <div className="py-12 text-center">
            <div className="spinner mx-auto mb-3"></div>
            <p className="text-sm text-muted">Carregando usuários...</p>
          </div>
        )}

        {!isLoading && filteredUsers.length === 0 && (
          <div className="py-12 text-center text-muted">
            <p className="text-base font-medium">Nenhum usuário encontrado.</p>
            <p className="text-sm mt-1">Crie um novo usuário ou ajuste os filtros.</p>
          </div>
        )}

        {!isLoading && filteredUsers.length > 0 && (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome & Identidade</th>
                  <th>Tenant / Aplicação</th>
                  <th>E-mail</th>
                  <th>Roles RBAC</th>
                  <th>Verificado</th>
                  <th>Cadastrado Em</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs uppercase shrink-0 border border-blue-200">
                          {u.name ? u.name.substring(0, 2) : "US"}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900 block">{u.name}</span>
                          {u.username && (
                            <span className="text-xs text-muted block font-mono">
                              @{u.username}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge variant="neutral" className="text-xs font-mono">
                        {appMap.get(u.application_id)?.name || u.application_id || "auth-admin"}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-sm text-gray-800">{u.email}</span>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {u.roles && u.roles.length > 0 ? (
                          u.roles.map((r) => (
                            <Badge key={r.id} variant="info" className="text-xs py-0">
                              {r.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted">Sem perfil</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {u.is_verified ? (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                          <CheckCircle size={14} />
                          <span>Sim</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                          <XCircle size={14} />
                          <span>Pendente</span>
                        </span>
                      )}
                    </td>
                    <td className="text-xs text-muted">{formatDate(u.created_at)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <PermissionGuard permission="users:update">
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => {
                              setUserToEdit(u);
                              setIsModalOpen(true);
                            }}
                            title="Editar usuário"
                            aria-label={`Editar usuário ${u.name}`}
                          >
                            <Edit2 size={16} />
                          </button>
                        </PermissionGuard>

                        <PermissionGuard permission="users:delete">
                          <button
                            type="button"
                            className="btn-icon text-red-500 hover:text-red-700"
                            onClick={() => setUserToDelete(u)}
                            title="Excluir usuário"
                            aria-label={`Excluir usuário ${u.name}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Create/Edit Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={loadUsers}
        userToEdit={userToEdit}
      />

      {/* Delete User Modal */}
      {userToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <button
            type="button"
            className="modal-overlay"
            onClick={() => setUserToDelete(null)}
            aria-label="Cancelar exclusão de usuário"
            tabIndex={-1}
          />
          <div className="modal-container modal-sm">
            <div className="modal-header">
              <h3 className="modal-title flex items-center gap-2 text-red-600">
                <ShieldAlert size={20} />
                <span>Confirmar Exclusão</span>
              </h3>
            </div>
            <div className="modal-body space-y-3">
              <p className="text-sm text-gray-700">
                Deseja realmente remover o usuário <strong>{userToDelete.name}</strong> (
                <code>{userToDelete.email}</code>)?
              </p>
              <div className="flex justify-end gap-3 pt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setUserToDelete(null)}
                  disabled={isDeleting}
                >
                  Cancelar
                </Button>
                <Button variant="danger" size="sm" onClick={handleDelete} isLoading={isDeleting}>
                  Confirmar Exclusão
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
